import { ComponentTypes, type ComponentType, type All8PageContent } from "@apa/core-renderers";
import type { ComponentDefinition } from "./pageComponentLibrary.types";
import { DEFAULT_PRERENDER_IMAGE } from "./pageComponentLibrary.utils";

const HOME_AUDIENCE_ICONS = [
	{ label: "User", value: "User" },
	{ label: "Building2", value: "Building2" },
	{ label: "Megaphone", value: "Megaphone" },
	{ label: "Landmark", value: "Landmark" },
	{ label: "FlaskConical", value: "FlaskConical" },
	{ label: "GraduationCap", value: "GraduationCap" }
];

const ICON_CARD_ICONS = [
	{ label: "ShieldCheck", value: "ShieldCheck" },
	{ label: "Building2", value: "Building2" },
	{ label: "Banknote", value: "Banknote" },
	{ label: "Database", value: "Database" },
	{ label: "Target", value: "Target" },
	{ label: "Gauge", value: "Gauge" },
	{ label: "Zap", value: "Zap" }
];

const withPreview = (type: ComponentType, content: any): All8PageContent =>
	[
		{
			componentType: type as any,
			key: `preview-${type}`,
			content
		}
	] as All8PageContent;

export const componentLibrary: ComponentDefinition[] = [
	{
		type: ComponentTypes.TextBlock,
		title: "Paragraph",
		description: "Rich paragraph block to add long-form text.",
		tags: ["text", "content"],
		defaultContent: {
			paragraphs: [
				"Paragraph 1",
				"Paragraph 2",
				"Paragraph 3"
			]
		},
		fields: [
			{
				type: "array",
				label: "Paragraphs",
				itemLabel: "Paragraph",
				itemType: "text",
				defaultItem: "",
				path: ["content", "paragraphs"],
				itemEditor: "richParagraph"
			}
		],
		previewComponents: withPreview(ComponentTypes.TextBlock, {
			paragraphs: [
				"Paragraph 1",
				"Paragraph 2",
				"Paragraph 3"
			]
		})
	},
	{
		type: ComponentTypes.DynamicComponents,
		title: "Dynamic Components",
		description: "Inject components from a dynamic content field (template-only).",
		tags: ["dynamic", "template", "components"],
		defaultContent: {
			variableKey: "components"
		},
		fields: [
			{
				type: "text",
				label: "Variable key",
				path: ["content", "variableKey"],
				placeholder: "components",
				helper: "Matches the field key on the selected dynamic content type."
			}
		],
		previewComponents: withPreview(ComponentTypes.DynamicComponents, {
			variableKey: "components"
		})
	},
	{
		type: ComponentTypes.AccordionSection,
		title: "Accordion Section",
		description: "Expandable items with title and rich paragraphs. Background #EDF7FC, title #005CA9, plus icon #FFA401.",
		tags: ["accordion", "faq", "content"],
		defaultContent: {
			items: [
				{ title: "Quel est le meilleur moment pour nous rendre visite", paragraphs: ["Réponse ou contenu de la première section."] },
				{ title: "Préparez votre visite", paragraphs: ["Contenu pour préparer la visite."] },
				{ title: "Itinéraire pour nous rejoindre", paragraphs: ["Instructions et itinéraire."] }
			]
		},
		fields: [
			{
				type: "array",
				label: "Items",
				itemLabel: "Item",
				itemType: "object",
				defaultItem: { title: "", paragraphs: [""] },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"], placeholder: "Titre de l’item" },
					{
						type: "array",
						label: "Paragraphs",
						itemLabel: "Paragraph",
						itemType: "text",
						defaultItem: "",
						path: ["paragraphs"],
						itemEditor: "richParagraph"
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.AccordionSection, {
			items: [
				{ title: "Quel est le meilleur moment pour nous rendre visite", paragraphs: ["Réponse ou contenu."] },
				{ title: "Préparez votre visite", paragraphs: ["Contenu."] },
				{ title: "Itinéraire pour nous rejoindre", paragraphs: ["Instructions."] }
			]
		})
	},
	{
		type: ComponentTypes.Coordonnees,
		title: "Coordonnées",
		description: "Split layout: image left (fit text height on desktop/tablet, min width on mobile), content right. Top title #005CA9, phone icon #FFA401, email icon #005CA9. No rounded corners.",
		tags: ["contact", "coordonnées", "image"],
		defaultContent: {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			topTitle: "Les coordonnées générales",
			introParagraph: "Pour toute demande générale, vous pouvez contacter la Banque Centrale du Congo via les coordonnées ci-dessous :",
			phoneHref: { external: "tel:+243123456789" },
			emailHref: { external: "mailto:contact@apa.cd" },
			secondTitle: "Adresse et horaires",
			addressLinkText: "563 Boulevard Colonel Tshatshi - Gombé - Kinshasa",
			addressLinkHref: { external: "https://maps.example.com" },
			addressLines: ["Boîte Postale : 2697 Kinshasa 1 République Démocratique du Congo"]
		},
		fields: [
			{ type: "image", label: "Image", path: ["content", "imageSrc"], helper: "Image gauche." },
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "Titre principal", path: ["content", "topTitle"], placeholder: "Les coordonnées générales" },
			{ type: "textarea", label: "Paragraphe d’intro", path: ["content", "introParagraph"] },
			{ type: "text", label: "Lien téléphone (ex: tel:+243...)", path: ["content", "phoneHref", "external"], placeholder: "tel:+243..." },
			{ type: "text", label: "Lien email (ex: mailto:...)", path: ["content", "emailHref", "external"], placeholder: "mailto:contact@..." },
			{ type: "text", label: "Titre adresse", path: ["content", "secondTitle"], placeholder: "Adresse et horaires" },
			{ type: "text", label: "Adresse (lien, texte)", path: ["content", "addressLinkText"], placeholder: "563 Boulevard..." },
			{ type: "text", label: "Adresse (lien, URL)", path: ["content", "addressLinkHref", "external"], placeholder: "https://..." },
			{
				type: "array",
				label: "Lignes adresse (supplément)",
				itemLabel: "Ligne",
				itemType: "text",
				defaultItem: "",
				path: ["content", "addressLines"]
			}
		],
		previewComponents: withPreview(ComponentTypes.Coordonnees, {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			topTitle: "Les coordonnées générales",
			introParagraph: "Pour toute demande générale, vous pouvez contacter la Banque Centrale du Congo via les coordonnées ci-dessous :",
			phoneHref: { external: "tel:+243123456789" },
			emailHref: { external: "mailto:contact@apa.cd" },
			secondTitle: "Adresse et horaires",
			addressLinkText: "563 Boulevard Colonel Tshatshi - Gombé - Kinshasa",
			addressLinkHref: { external: "#" },
			addressLines: ["Boîte Postale : 2697 Kinshasa 1 République Démocratique du Congo"]
		})
	},
	{
		type: ComponentTypes.BlueCallout,
		title: "Blue Callout",
		description: "Two-column layout: left panel #0095DB with white title, 16px paragraph, and white chevron; right = image. Optional link.",
		tags: ["callout", "image", "blue"],
		defaultContent: {
			title: "Stabilité et dynamisme : la stratégie monétaire de la APA",
			text: "Pendant longtemps, le taux directeur à 25% a freiné l'inflation et stabilisé la monnaie. Les réserves obligatoires à 12-13% régulent la liquidité bancaire, tandis que les réserves de change couvrent 3 mois d'importations.",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image"
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Text", path: ["content", "text"] },
			{ type: "image", label: "Image (right)", path: ["content", "imageSrc"], helper: "Image displayed on the right." },
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "Link slug (optional)", path: ["content", "ctaHref", "slug"], placeholder: "/path" },
			{ type: "text", label: "Link external URL (optional)", path: ["content", "ctaHref", "external"], placeholder: "https://..." }
		],
		previewComponents: withPreview(ComponentTypes.BlueCallout, {
			title: "Stabilité et dynamisme : la stratégie monétaire de la APA",
			text: "Pendant longtemps, le taux directeur à 25% a freiné l'inflation et stabilisé la monnaie.",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image"
		})
	},
	{
		type: ComponentTypes.Margin,
		title: "Margin / Spacer",
		description: "Add vertical spacing between sections.",
		tags: ["layout", "spacing"],
		defaultContent: {
			mobile: "mt-8",
			md: "md:mt-10",
			lg: "lg:mt-12"
		},
		fields: [
			{
				type: "text",
				label: "Mobile spacing",
				path: ["content", "mobile"],
				placeholder: "mt-8",
				helper: "Tailwind margin-top class for mobile."
			},
			{
				type: "text",
				label: "MD spacing",
				path: ["content", "md"],
				placeholder: "md:mt-10",
				helper: "Tailwind margin-top class for tablet (md)."
			},
			{
				type: "text",
				label: "LG spacing",
				path: ["content", "lg"],
				placeholder: "lg:mt-12",
				helper: "Tailwind margin-top class for desktop (lg)."
			}
		],
		previewComponents: [
			{
				componentType: ComponentTypes.SectionHeading,
				key: "preview-margin-top",
				content: { text: "Section A", size: "medium" }
			},
			{
				componentType: ComponentTypes.Margin,
				key: "preview-margin",
				content: { mobile: "mt-8", md: "md:mt-10", lg: "lg:mt-12" }
			},
			{
				componentType: ComponentTypes.SectionHeading,
				key: "preview-margin-bottom",
				content: { text: "Section B", size: "medium" }
			}
		] as All8PageContent
	},
	{
		type: ComponentTypes.PageHeader,
		title: "Page Header",
		description: "Header with title only.",
		tags: ["intro", "header"],
		defaultContent: {
			title: "Page title"
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] }
		],
		previewComponents: withPreview(ComponentTypes.PageHeader, {
			title: "Page title"
		})
	},
	{
		type: ComponentTypes.Hero,
		title: "Hero",
		description: "Large landing hero with background image and lead copy.",
		tags: ["landing", "home"],
		defaultContent: {
			eyebrow: "Pièces diesel · Échange standard",
			title: "La performance d’origine. Le prix du reconditionné.",
			subtitle: "Turbos, injecteurs et FAP/DPF contrôlés dans nos ateliers, garantis jusqu’à 12 mois et expédiés sous 24/48h.",
			body: "Hero body text",
			primaryLabel: "Trouver ma pièce",
			primaryHref: { slug: "pieces-detachees" },
			secondaryLabel: "Parler à un spécialiste",
			secondaryHref: { slug: "contact" },
			assurances: ["Neuf ou reconditionné", "Garantie jusqu’à 12 mois", "Expédition 24/48h"],
			backgroundImageSrc: "images/photos/landing-apa-team.jpg",
			mobileBackgroundImageSrc: "images/photos/landing-apa-team-mobile.jpg"
		},
		fields: [
			{ type: "text", label: "Eyebrow", path: ["content", "eyebrow"] },
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "text", label: "Subtitle", path: ["content", "subtitle"] },
			{ type: "textarea", label: "Body", path: ["content", "body"] },
			{ type: "text", label: "Primary CTA label", path: ["content", "primaryLabel"] },
			{ type: "href", label: "Primary CTA destination", path: ["content", "primaryHref"] },
			{ type: "text", label: "Secondary CTA label", path: ["content", "secondaryLabel"] },
			{ type: "href", label: "Secondary CTA destination", path: ["content", "secondaryHref"] },
			{
				type: "array",
				label: "Assurances",
				itemLabel: "Assurance",
				itemType: "text",
				defaultItem: "",
				path: ["content", "assurances"]
			},
			{
				type: "image",
				label: "Background image",
				path: ["content", "backgroundImageSrc"],
				helper: "Select an image from storage (saved as a CDN key without the domain)."
			},
			{
				type: "image",
				label: "Mobile background image",
				path: ["content", "mobileBackgroundImageSrc"],
				helper: "Portrait composition used below the desktop breakpoint."
			}
		],
		previewComponents: withPreview(ComponentTypes.Hero, {
			eyebrow: "Pièces diesel · Échange standard",
			title: "La performance d’origine. Le prix du reconditionné.",
			subtitle: "Turbos, injecteurs et FAP/DPF contrôlés dans nos ateliers, garantis jusqu’à 12 mois et expédiés sous 24/48h.",
			body: "Hero body text",
			primaryLabel: "Trouver ma pièce",
			primaryHref: { slug: "pieces-detachees" },
			secondaryLabel: "Parler à un spécialiste",
			secondaryHref: { slug: "contact" },
			assurances: ["Neuf ou reconditionné", "Garantie jusqu’à 12 mois", "Expédition 24/48h"],
			backgroundImageSrc: "images/photos/landing-apa-team.jpg",
			mobileBackgroundImageSrc: "images/photos/landing-apa-team-mobile.jpg"
		})
	},
	{
		type: ComponentTypes.NewsSection,
		title: "News Section",
		description: "Featured story with supporting headlines.",
		tags: ["news", "home", "dynamic"],
		defaultContent: {
			mode: "manual",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				featuredFilter: [],
				itemsFilter: [],
				featuredCount: 1,
				itemsCount: 3,
				fieldMapping: {
					title: "",
					excerpt: "",
					image: "",
					href: ""
				}
			},
			featured: {
				id: "feature-1",
				title: "Featured title",
				excerpt: "Featured excerpt",
				image: DEFAULT_PRERENDER_IMAGE,
				href: { slug: "/news/featured" }
			},
			items: [
				{
					id: "news-1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-1" }
				},
				{
					id: "news-2",
					title: "News title 2",
					excerpt: "News excerpt 2",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-2" }
				}
			]
		},
		fields: [
			{
				type: "select",
				label: "Mode",
				path: ["content", "mode"],
				options: [
					{ label: "Manual", value: "manual" },
					{ label: "Dynamic", value: "dynamic" }
				],
				helper: "Manual uses the content below. Dynamic pulls items from a content type."
			},
			{
				type: "object",
				label: "Dynamic settings",
				path: ["content", "dynamicComponent"],
				showWhen: { path: ["content", "mode"], equals: "dynamic" },
				fields: [
					{
						type: "contentTypeSelect",
						label: "Content type",
						path: ["contentTypeKey"],
						helper: "Select the content type to pull items from."
					},
					{
						type: "dynamicPageSelect",
						label: "Dynamic page",
						path: ["dynamicPageId"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						allowEmpty: true,
						helper: "Pick the dynamic page template to link to (same content type)."
					},
					{
						type: "contentFieldSelect",
						label: "Sort field (date)",
						path: ["sortField"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						fieldTypes: ["date"],
						includeBaseFields: false,
						allowEmpty: true,
						valueFormat: "raw",
						helper: "Used for both featured and items (latest first)."
					},
					{
						type: "number",
						label: "Featured count",
						path: ["featuredCount"],
						helper: "Number of candidates for the featured slot."
					},
					{
						type: "number",
						label: "Items count",
						path: ["itemsCount"],
						helper: "Number of items to show in the list."
					},
					{
						type: "array",
						label: "Featured filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["featuredFilter"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "array",
						label: "Items filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["itemsFilter"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "object",
						label: "Field mapping",
						path: ["fieldMapping"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Title field",
								path: ["title"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the title."
							},
							{
								type: "contentFieldSelect",
								label: "Description field",
								path: ["excerpt"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the excerpt."
							},
							{
								type: "contentFieldSelect",
								label: "Image field",
								path: ["image"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["image"],
								includeBaseFields: false,
								valueFormat: "raw",
								helper: "Select the image field to use."
							},
							{
								type: "contentFieldSelect",
								label: "Link field",
								path: ["href"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text", "url"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field used for the link (slug/path/url)."
							}
						]
					}
				]
			},
			{
				type: "object",
				label: "Featured story",
				path: ["content", "featured"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{
						type: "image",
						label: "Image",
						path: ["image"],
						helper: "Pick an image from storage (CDN key only)."
					},
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an existing page or an external URL."
					}
				]
			},
			{
				type: "array",
				label: "News items",
				itemLabel: "News",
				itemType: "object",
				defaultItem: { title: "", excerpt: "", image: DEFAULT_PRERENDER_IMAGE, href: { slug: "" } },
				path: ["content", "items"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{
						type: "image",
						label: "Image",
						path: ["image"],
						helper: "Pick an image from storage (CDN key only)."
					},
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a site page or paste an external URL."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.NewsSection, {
			mode: "manual",
			dynamicComponent: {
				contentTypeKey: "",
				sortField: "",
				featuredFilter: [],
				itemsFilter: [],
				featuredCount: 1,
				itemsCount: 3,
				fieldMapping: {
					title: "",
					excerpt: "",
					image: "",
					href: ""
				}
			},
			featured: {
				id: "feature-1",
				title: "Featured title",
				excerpt: "Featured excerpt",
				image: DEFAULT_PRERENDER_IMAGE,
				href: { slug: "/news/featured" }
			},
			items: [
				{
					id: "news-1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-1" }
				},
				{
					id: "news-2",
					title: "News title 2",
					excerpt: "News excerpt 2",
					image: DEFAULT_PRERENDER_IMAGE,
					href: { slug: "/news/item-2" }
				}
			]
		})
	},
	{
		type: ComponentTypes.NewsList,
		title: "News List",
		description: "Vertical list of news items with optional gray panels.",
		tags: ["news", "list"],
		defaultContent: {
			useGrayPanel: true,
			items: [
				{
					tag: "TAG 1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					date: "01/2025",
					href: { slug: "/news/item-1" }
				},
				{
					tag: "TAG 2",
					title: "News title 2",
					meta: "Meta line",
					date: "02/2025",
					href: { slug: "/news/item-2" }
				}
			],
			ctaLabel: "All news",
			ctaHref: { slug: "/news" }
		},
		fields: [
			{
				type: "select",
				label: "Gray panel background",
				path: ["content", "useGrayPanel"],
				options: [
					{ label: "Yes (use #F5F5F5 panel)", value: "true" },
					{ label: "No (transparent)", value: "false" }
				]
			},
			{
				type: "array",
				label: "News items",
				itemLabel: "News",
				itemType: "object",
				defaultItem: { tag: "", title: "", excerpt: "", meta: "", date: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Tag", path: ["tag"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{ type: "text", label: "Meta line", path: ["meta"], helper: "Optional subtitle (location, timeframe, etc.)." },
					{ type: "text", label: "Date", path: ["date"], placeholder: "DD/MM/YYYY" },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a page or paste an external URL."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the news listing page."
			}
		],
		previewComponents: withPreview(ComponentTypes.NewsList, {
			useGrayPanel: true,
			items: [
				{
					tag: "TAG 1",
					title: "News title 1",
					excerpt: "News excerpt 1",
					date: "01/2025",
					href: { slug: "/news/item-1" }
				},
				{
					tag: "TAG 2",
					title: "News title 2",
					meta: "Meta line",
					date: "02/2025",
					href: { slug: "/news/item-2" }
				}
			],
			ctaLabel: "All news",
			ctaHref: { slug: "/news" }
		})
	},
	{
		type: ComponentTypes.StatsSummary,
		title: "Stats Summary",
		description: "Key rates and surveys at a glance.",
		tags: ["stats", "home"],
		defaultContent: {
			interestRates: [
				{ label: "Rate 1", value: "0.00%" },
				{ label: "Rate 2", value: "0.00%" }
			],
			exchangeRates: [
				{ code: "CUR", value: "0.00" },
				{ code: "CUR", value: "0.00" }
			],
			surveys: [
				{ label: "Survey 1", value: "0" },
				{ label: "Survey 2", value: "0" }
			]
		},
		fields: [
			{
				type: "array",
				label: "Interest rates",
				itemLabel: "Rate",
				itemType: "object",
				defaultItem: { label: "", value: "" },
				path: ["content", "interestRates"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{ type: "text", label: "Value", path: ["value"], placeholder: "0.00%" }
				]
			},
			{
				type: "array",
				label: "Exchange rates",
				itemLabel: "Rate",
				itemType: "object",
				defaultItem: { code: "", value: "" },
				path: ["content", "exchangeRates"],
				fields: [
					{ type: "text", label: "Code", path: ["code"], placeholder: "CUR" },
					{ type: "text", label: "Value", path: ["value"], placeholder: "0.00" }
				]
			},
			{
				type: "array",
				label: "Surveys",
				itemLabel: "Survey",
				itemType: "object",
				defaultItem: { label: "", value: "" },
				path: ["content", "surveys"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{ type: "text", label: "Value", path: ["value"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.StatsSummary, {
			interestRates: [
				{ label: "Rate 1", value: "0.00%" },
				{ label: "Rate 2", value: "0.00%" }
			],
			exchangeRates: [
				{ code: "CUR", value: "0.00" },
				{ code: "CUR", value: "0.00" }
			],
			surveys: [
				{ label: "Survey 1", value: "0" },
				{ label: "Survey 2", value: "0" }
			]
		})
	},
	{
		type: ComponentTypes.SectionHeading,
		title: "Section Heading",
		description: "Standalone heading to separate sections.",
		tags: ["heading", "text"],
		defaultContent: {
			text: "Section heading",
			size: "large"
		},
		fields: [
			{ type: "text", label: "Text", path: ["content", "text"] },
			{
				type: "select",
				label: "Size",
				path: ["content", "size"],
				options: [
					{ label: "Large", value: "large" },
					{ label: "Medium", value: "medium" }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.SectionHeading, { text: "Section heading", size: "large" })
	},
	{
		type: ComponentTypes.Callout,
		title: "Callout",
		description: "Compact call-to-action box with a link.",
		tags: ["cta", "highlight"],
		defaultContent: {
			title: "Callout title",
			linkLabel: "Callout link",
			linkHref: { slug: "/path" }
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "text", label: "Link label", path: ["content", "linkLabel"] },
			{
				type: "href",
				label: "Link destination",
				path: ["content", "linkHref"],
				helper: "Point to a page, manual path, or external URL."
			}
		],
		previewComponents: withPreview(ComponentTypes.Callout, {
			title: "Callout title",
			linkLabel: "Callout link",
			linkHref: "#"
		})
	},
	{
		type: ComponentTypes.CtaBanner,
		title: "CTA Banner",
		description: "Banner with title and a CTA link.",
		tags: ["cta", "banner"],
		defaultContent: {
			title: "Banner title",
			linkLabel: "Learn more",
			linkHref: { slug: "/link" }
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "text", label: "Link label", path: ["content", "linkLabel"] },
			{
				type: "href",
				label: "Link destination",
				path: ["content", "linkHref"],
				helper: "Select a page or paste an external URL."
			}
		],
		previewComponents: withPreview(ComponentTypes.CtaBanner, {
			title: "Banner title",
			linkLabel: "Learn more",
			linkHref: { slug: "/link" }
		})
	},
	{
		type: ComponentTypes.LinkTileGrid,
		title: "Link Tile Grid",
		description: "Grid of simple link tiles.",
		tags: ["links", "grid"],
		defaultContent: {
			items: [
				{ label: "Link 1", href: { slug: "/link-1" } },
				{ label: "Link 2", href: { slug: "/link-2" } },
				{ label: "Link 3", href: { slug: "/link-3" } },
				{ label: "Link 4", href: { slug: "/link-4" } }
			]
		},
		fields: [
			{
				type: "array",
				label: "Links",
				itemLabel: "Link",
				itemType: "object",
				defaultItem: { label: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Pick an internal page or external link for this tile."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.LinkTileGrid, {
			items: [
				{ label: "Link 1", href: { slug: "/link-1" } },
				{ label: "Link 2", href: { slug: "/link-2" } },
				{ label: "Link 3", href: { slug: "/link-3" } },
				{ label: "Link 4", href: { slug: "/link-4" } }
			]
		})
	},
	{
		type: ComponentTypes.AudienceTools,
		title: "Audience Tools",
		description: "Quick entry points by audience segment.",
		tags: ["home", "links"],
		defaultContent: {
			searchPlaceholder: "Search",
			audiences: [
				{ icon: "User", label: "Audience 1", href: { slug: "/audience-1" } },
				{ icon: "Building2", label: "Audience 2", href: { slug: "/audience-2" } },
				{ icon: "Megaphone", label: "Audience 3", href: { slug: "/audience-3" } }
			]
		},
		fields: [
			{ type: "text", label: "Search placeholder", path: ["content", "searchPlaceholder"] },
			{
				type: "array",
				label: "Audiences",
				itemLabel: "Audience",
				itemType: "object",
				defaultItem: { icon: "User", label: "", href: { slug: "" } },
				path: ["content", "audiences"],
				fields: [
					{ type: "select", label: "Icon", path: ["icon"], options: HOME_AUDIENCE_ICONS },
					{ type: "text", label: "Label", path: ["label"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Internal audience page or external URL."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.AudienceTools, {
			searchPlaceholder: "Search",
			audiences: [
				{ icon: "User", label: "Audience 1", href: { slug: "/audience-1" } },
				{ icon: "Building2", label: "Audience 2", href: { slug: "/audience-2" } },
				{ icon: "Megaphone", label: "Audience 3", href: { slug: "/audience-3" } }
			]
		})
	},
	{
		type: ComponentTypes.SearchBar,
		title: "Search Bar",
		description: "Light blue bar with title and centered search field. Bold uses secondary blue background and white text.",
		tags: ["home", "search"],
		defaultContent: {
			title: "Search",
			searchPlaceholder: "Search",
			bold: false
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"], placeholder: "Search title" },
			{ type: "text", label: "Search placeholder", path: ["content", "searchPlaceholder"], placeholder: "Search" },
			{
				type: "select",
				label: "Bold style",
				path: ["content", "bold"],
				options: [
					{ label: "No (light blue bar)", value: "false" },
					{ label: "Yes (blue #005CA9, white text)", value: "true" }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.SearchBar, {
			title: "Search",
			searchPlaceholder: "Search",
			bold: false
		})
	},
	{
		type: ComponentTypes.PublicationsUpdates,
		title: "Publications + Updates",
		description: "Dual column feed of publications and stats updates.",
		tags: ["home", "content"],
		defaultContent: {
			backgroundImageSrc: DEFAULT_PRERENDER_IMAGE,
			publications: [
				{
					id: "pub-1",
					tag: "Tag 1",
					date: "01/2025",
					title: "Publication title 1",
					href: { slug: "/publications/item-1" }
				},
				{
					id: "pub-2",
					tag: "Tag 2",
					date: "02/2025",
					title: "Publication title 2",
					href: { slug: "/publications/item-2" }
				}
			],
			statUpdates: [
				{
					category: "Category",
					period: "2025-01",
					items: [
						{ title: "Update 1" },
						{ title: "Update 2", subtitle: "" }
					]
				}
			]
		},
		fields: [
			{
				type: "image",
				label: "Background image",
				path: ["content", "backgroundImageSrc"],
				helper: "Select a background image from storage (CDN key only)."
			},
			{
				type: "array",
				label: "Publications",
				itemLabel: "Publication",
				itemType: "object",
				defaultItem: { id: "", tag: "", date: "", title: "", href: { slug: "" } },
				path: ["content", "publications"],
				fields: [
					{ type: "text", label: "Id", path: ["id"] },
					{ type: "text", label: "Tag", path: ["tag"] },
					{ type: "text", label: "Date", path: ["date"], placeholder: "MM/YYYY" },
					{ type: "text", label: "Title", path: ["title"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a publication page or paste an external link."
					}
				]
			},
			{
				type: "array",
				label: "Stat updates",
				itemLabel: "Category",
				itemType: "object",
				defaultItem: { category: "", period: "", items: [] },
				path: ["content", "statUpdates"],
				fields: [
					{ type: "text", label: "Category", path: ["category"] },
					{ type: "text", label: "Period", path: ["period"], placeholder: "YYYY-MM" },
					{
						type: "array",
						label: "Items",
						itemLabel: "Entry",
						itemType: "object",
						defaultItem: { title: "", subtitle: "" },
						path: ["items"],
						fields: [
							{ type: "text", label: "Title", path: ["title"] },
							{ type: "text", label: "Subtitle", path: ["subtitle"], placeholder: "Optional" }
						]
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.PublicationsUpdates, {
			backgroundImageSrc: DEFAULT_PRERENDER_IMAGE,
			publications: [
				{
					id: "pub-1",
					tag: "Tag 1",
					date: "01/2025",
					title: "Publication title 1",
					href: { slug: "/publications/item-1" }
				},
				{
					id: "pub-2",
					tag: "Tag 2",
					date: "02/2025",
					title: "Publication title 2",
					href: { slug: "/publications/item-2" }
				}
			],
			statUpdates: [
				{
					category: "Category",
					period: "2025-01",
					items: [
						{ title: "Update 1" },
						{ title: "Update 2", subtitle: "" }
					]
				}
			]
		})
	},
	{
		type: ComponentTypes.ActivityGrid,
		title: "Activity Grid",
		description: "Six-card grid from Activités section.",
		tags: ["grid", "activities"],
		defaultContent: {
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		},
		fields: [
			{
				type: "array",
				label: "Activity items",
				itemLabel: "Activity",
				itemType: "object",
				defaultItem: { title: "", description: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Description", path: ["description"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an activity page or external destination."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.ActivityGrid, {
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		})
	},
	{
		type: ComponentTypes.PaginatedActivityGrid,
		title: "Paginated Activity Grid",
		description: "Dynamic ActivityGrid with search, taxonomy/category filters, and pagination.",
		tags: ["grid", "activities", "dynamic", "pagination", "card"],
		defaultContent: {
			mode: "dynamic",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				filters: [],
				itemsPerPage: 24,
				enableTaxonomyFilter: "true",
				enableCategoryFilter: "true",
				searchPlaceholder: "Rechercher",
				cardType: "ActivityGrid",
				fieldMapping: {
					title: "",
					description: "",
					image: "",
					imageAlt: "",
					href: ""
				}
			},
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			pagination: {
				itemsPerPage: 24,
				totalItems: 2,
				totalPages: 1,
				page: 1,
				query: "",
				taxonomyId: "",
				categoryId: "",
				taxonomies: [],
				categories: []
			},
			enableTaxonomyFilter: true,
			enableCategoryFilter: true,
			searchPlaceholder: "Rechercher",
			cardType: "ActivityGrid"
		},
		fields: [
			{
				type: "select",
				label: "Mode",
				path: ["content", "mode"],
				options: [
					{ label: "Manual", value: "manual" },
					{ label: "Dynamic", value: "dynamic" }
				],
				helper: "Manual uses the content below. Dynamic pulls items from a content type."
			},
			{
				type: "object",
				label: "Dynamic settings",
				path: ["content", "dynamicComponent"],
				showWhen: { path: ["content", "mode"], equals: "dynamic" },
				fields: [
					{
						type: "contentTypeSelect",
						label: "Content type",
						path: ["contentTypeKey"],
						helper: "Select the content type to pull items from."
					},
					{
						type: "dynamicPageSelect",
						label: "Dynamic page",
						path: ["dynamicPageId"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						allowEmpty: true,
						helper: "Pick the dynamic page template to link to (same content type)."
					},
					{
						type: "contentFieldSelect",
						label: "Sort field (date)",
						path: ["sortField"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						fieldTypes: ["date"],
						includeBaseFields: false,
						allowEmpty: true,
						valueFormat: "raw",
						helper: "Latest first (used for pagination order)."
					},
					{
						type: "array",
						label: "Filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["filters"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "number",
						label: "Items per page",
						path: ["itemsPerPage"],
						helper: "Number of cards shown per page (default 24)."
					},
					{
						type: "select",
						label: "Taxonomy filter",
						path: ["enableTaxonomyFilter"],
						options: [
							{ label: "Enabled", value: "true" },
							{ label: "Disabled", value: "false" }
						],
						helper: "Show taxonomy filter dropdown."
					},
					{
						type: "select",
						label: "Category filter",
						path: ["enableCategoryFilter"],
						options: [
							{ label: "Enabled", value: "true" },
							{ label: "Disabled", value: "false" }
						],
						helper: "Show category filter dropdown."
					},
					{
						type: "text",
						label: "Search placeholder",
						path: ["searchPlaceholder"],
						helper: "Label shown inside the search input."
					},
					{
						type: "select",
						label: "Card layout",
						path: ["cardType"],
						options: [{ label: "Activity Grid", value: "ActivityGrid" }],
						helper: "Additional layouts will be added later."
					},
					{
						type: "object",
						label: "Field mapping",
						path: ["fieldMapping"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Title field",
								path: ["title"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the title."
							},
							{
								type: "contentFieldSelect",
								label: "Description field",
								path: ["description"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field to use for the description."
							},
							{
								type: "contentFieldSelect",
								label: "Image field",
								path: ["image"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["image"],
								includeBaseFields: false,
								valueFormat: "raw",
								helper: "Select the image field to use."
							},
							{
								type: "contentFieldSelect",
								label: "Image alt field",
								path: ["imageAlt"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Optional alt text field (falls back to title)."
							},
							{
								type: "contentFieldSelect",
								label: "Link field",
								path: ["href"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text", "url"],
								includeBaseFields: true,
								valueFormat: "raw",
								helper: "Select the field used for the link (slug/path/url)."
							}
						]
					}
				]
			},
			{
				type: "array",
				label: "Activity items",
				itemLabel: "Activity",
				itemType: "object",
				defaultItem: { title: "", description: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Description", path: ["description"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an activity page or external destination."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.PaginatedActivityGrid, {
			mode: "dynamic",
			items: [
				{
					title: "Activity 1",
					description: "Activity description 1",
					href: { slug: "/activity-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Activity 2",
					description: "Activity description 2",
					href: { slug: "/activity-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			pagination: {
				itemsPerPage: 24,
				totalItems: 2,
				totalPages: 1,
				page: 1,
				query: "",
				taxonomyId: "",
				categoryId: "",
				taxonomies: [],
				categories: []
			},
			enableTaxonomyFilter: true,
			enableCategoryFilter: true,
			searchPlaceholder: "Rechercher",
			cardType: "ActivityGrid"
		})
	},
	{
		type: ComponentTypes.PersonCardGrid,
		title: "Person Cards Grid",
		description: "Grid of person/role cards with subtitle in #0095DB and no arrow.",
		tags: ["grid", "people", "roles", "card"],
		defaultContent: {
			items: [
				{
					title: "Person 1",
					description: "Role or title",
					href: { slug: "/person-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Person 2",
					description: "Role or title",
					href: { slug: "/person-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		},
		fields: [
			{
				type: "array",
				label: "Person items",
				itemLabel: "Person",
				itemType: "object",
				defaultItem: { title: "", description: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Name", path: ["title"] },
					{ type: "textarea", label: "Role / subtitle", path: ["description"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose a profile page or external destination."
					},
					{
						type: "image",
						label: "Image",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.PersonCardGrid, {
			items: [
				{
					title: "Person 1",
					description: "Role or title",
					href: { slug: "/person-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					title: "Person 2",
					description: "Role or title",
					href: { slug: "/person-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			]
		})
	},
	{
		type: ComponentTypes.EventGrid,
		title: "Event Grid",
		description: "Compact list of upcoming events.",
		tags: ["events"],
		defaultContent: {
			items: [
				{
					tag: "EVENT",
					title: "Event title 1",
					location: "Location",
					href: { slug: "/events/event-1" },
					startDay: 1,
					endDay: 1,
					monthLabel: "JAN"
				},
				{
					tag: "EVENT",
					title: "Event title 2",
					location: "Location",
					href: { slug: "/events/event-2" },
					startDay: 2,
					endDay: 2,
					monthLabel: "FEB"
				}
			],
			ctaLabel: "All events",
			ctaHref: { slug: "/events" }
		},
		fields: [
			{
				type: "array",
				label: "Events",
				itemLabel: "Event",
				itemType: "object",
				defaultItem: {
					tag: "",
					title: "",
					location: "",
					href: { slug: "" },
					startDay: 1,
					endDay: 1,
					monthLabel: ""
				},
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Tag", path: ["tag"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "Location", path: ["location"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Choose an event page or external registration link."
					},
					{ type: "number", label: "Start day", path: ["startDay"] },
					{ type: "number", label: "End day", path: ["endDay"] },
					{ type: "text", label: "Month label", path: ["monthLabel"], placeholder: "MMM" }
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"], placeholder: "CTA label" },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the events listing or an external calendar page."
			}
		],
		previewComponents: withPreview(ComponentTypes.EventGrid, {
			items: [
				{
					tag: "EVENT",
					title: "Event title 1",
					location: "Location",
					href: { slug: "/events/event-1" },
					startDay: 1,
					endDay: 1,
					monthLabel: "JAN"
				},
				{
					tag: "EVENT",
					title: "Event title 2",
					location: "Location",
					href: { slug: "/events/event-2" },
					startDay: 2,
					endDay: 2,
					monthLabel: "FEB"
				}
			],
			ctaLabel: "All events",
			ctaHref: { slug: "/events" }
		})
	},
	{
		type: ComponentTypes.ArticleGrid,
		title: "Article Grid",
		description: "Four article cards with author metadata.",
		tags: ["articles", "grid"],
		defaultContent: {
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		},
		fields: [
			{
				type: "array",
				label: "Articles",
				itemLabel: "Article",
				itemType: "object",
				defaultItem: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					href: { slug: "" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Category", path: ["category"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{ type: "text", label: "Author", path: ["author"] },
					{ type: "text", label: "Author role", path: ["authorRole"] },
					{ type: "text", label: "Date", path: ["date"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Link to an article page or external source."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"], placeholder: "CTA label" },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the blog index or another page."
			}
		],
		previewComponents: withPreview(ComponentTypes.ArticleGrid, {
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		})
	},
	{
		type: ComponentTypes.LatestArticleGrid,
		title: "Latest Article Grid",
		description: "Article grid that can auto-populate latest items from a content type.",
		tags: ["articles", "grid", "dynamic"],
		defaultContent: {
			mode: "dynamic",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				filters: [],
				itemsCount: 3,
				fieldMapping: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					image: "",
					href: ""
				}
			},
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		},
		fields: [
			{
				type: "select",
				label: "Mode",
				path: ["content", "mode"],
				options: [
					{ label: "Manual", value: "manual" },
					{ label: "Dynamic", value: "dynamic" }
				],
				helper: "Manual uses content below. Dynamic pulls latest items from a content type."
			},
			{
				type: "object",
				label: "Dynamic settings",
				path: ["content", "dynamicComponent"],
				showWhen: { path: ["content", "mode"], equals: "dynamic" },
				fields: [
					{
						type: "contentTypeSelect",
						label: "Content type",
						path: ["contentTypeKey"],
						helper: "Select the content type to pull article items from."
					},
					{
						type: "dynamicPageSelect",
						label: "Dynamic page",
						path: ["dynamicPageId"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						allowEmpty: true,
						helper: "Optional dynamic page template used to build article links."
					},
					{
						type: "contentFieldSelect",
						label: "Sort field (date)",
						path: ["sortField"],
						contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
						fieldTypes: ["date"],
						includeBaseFields: false,
						allowEmpty: true,
						valueFormat: "raw",
						helper: "Latest first. Falls back to last updated when empty."
					},
					{
						type: "number",
						label: "Items count",
						path: ["itemsCount"],
						helper: "Number of latest cards to show (default 3)."
					},
					{
						type: "array",
						label: "Filters",
						itemLabel: "Filter",
						itemType: "object",
						defaultItem: { field: "", value: "" },
						path: ["filters"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Field",
								path: ["field"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldValue",
								label: "Value",
								path: ["value"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldPath: ["field"]
							}
						]
					},
					{
						type: "object",
						label: "Field mapping",
						path: ["fieldMapping"],
						fields: [
							{
								type: "contentFieldSelect",
								label: "Category field",
								path: ["category"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Title field",
								path: ["title"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Excerpt field",
								path: ["excerpt"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Author field",
								path: ["author"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Author role field",
								path: ["authorRole"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Date field",
								path: ["date"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["date", "text"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Image field",
								path: ["image"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["image"],
								includeBaseFields: false,
								allowEmpty: true,
								valueFormat: "raw"
							},
							{
								type: "contentFieldSelect",
								label: "Link field",
								path: ["href"],
								contentTypePath: ["content", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["text", "url"],
								includeBaseFields: true,
								allowEmpty: true,
								valueFormat: "raw"
							}
						]
					}
				]
			},
			{
				type: "array",
				label: "Articles",
				itemLabel: "Article",
				itemType: "object",
				defaultItem: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					href: { slug: "" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				path: ["content", "items"],
				showWhen: { path: ["content", "mode"], equals: "manual" },
				fields: [
					{ type: "text", label: "Category", path: ["category"] },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{ type: "text", label: "Author", path: ["author"] },
					{ type: "text", label: "Author role", path: ["authorRole"] },
					{ type: "text", label: "Date", path: ["date"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Link to an article page or external source."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"], placeholder: "CTA label" },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Link to the blog index or another page."
			}
		],
		previewComponents: withPreview(ComponentTypes.LatestArticleGrid, {
			mode: "dynamic",
			dynamicComponent: {
				contentTypeKey: "",
				dynamicPageId: "",
				sortField: "",
				filters: [],
				itemsCount: 3,
				fieldMapping: {
					category: "",
					title: "",
					excerpt: "",
					author: "",
					authorRole: "",
					date: "",
					image: "",
					href: ""
				}
			},
			items: [
				{
					category: "Category",
					title: "Article title 1",
					excerpt: "Article excerpt 1",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					category: "Category",
					title: "Article title 2",
					excerpt: "Article excerpt 2",
					author: "Author",
					authorRole: "Role",
					date: "01 Jan 2025",
					href: { slug: "/articles/article-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View all",
			ctaHref: { slug: "/articles" }
		})
	},
	{
		type: ComponentTypes.IconCardGrid,
		title: "Icon Card Grid",
		description: "Policy pillars represented as icon tiles.",
		tags: ["icons", "grid", "card"],
		defaultContent: {
			items: [
				{ icon: "ShieldCheck", title: "Card title 1", text: "Card text 1" },
				{ icon: "Gauge", title: "Card title 2", text: "Card text 2" },
				{ icon: "Banknote", title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2,
			centerLastRow: false
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Center last row",
				path: ["content", "centerLastRow"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "When the last row has fewer items, center them. Default: no."
			},
			{
				type: "array",
				label: "Icon cards",
				itemLabel: "Card",
				itemType: "object",
				defaultItem: { icon: "ShieldCheck", title: "", text: "" },
				path: ["content", "items"],
				fields: [
					{ type: "select", label: "Icon", path: ["icon"], options: ICON_CARD_ICONS },
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Text", path: ["text"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.IconCardGrid, {
			items: [
				{ icon: "ShieldCheck", title: "Card title 1", text: "Card text 1" },
				{ icon: "Gauge", title: "Card title 2", text: "Card text 2" },
				{ icon: "Banknote", title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2
		})
	},
	{
		type: ComponentTypes.StatsGrid,
		title: "Stats Grid",
		description: "KPI tiles for key stats.",
		tags: ["stats", "grid"],
		defaultContent: {
			items: [
				{ title: "Metric 1", value: "0", meta: "Meta" },
				{ title: "Metric 2", value: "0", meta: "Meta" },
				{ title: "Metric 3", value: "0", meta: "Meta" }
			]
		},
		fields: [
			{
				type: "array",
				label: "Stat items",
				itemLabel: "Stat",
				itemType: "object",
				defaultItem: { title: "", value: "", meta: "" },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "Value", path: ["value"] },
					{ type: "text", label: "Meta", path: ["meta"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.StatsGrid, {
			items: [
				{ title: "Metric 1", value: "0", meta: "Meta" },
				{ title: "Metric 2", value: "0", meta: "Meta" },
				{ title: "Metric 3", value: "0", meta: "Meta" }
			]
		})
	},
	{
		type: ComponentTypes.SplitImageCallout,
		title: "Split Image Callout",
		description: "Two-column layout with CTA and rich image.",
		tags: ["callout", "image"],
		defaultContent: {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			title: "Callout title",
			text: "Callout text",
			ctaLabel: "Learn more",
			ctaHref: { slug: "/callout" },
			calloutVariant: "default",
			panelBackground: "default",
			buttonColor: "default"
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "imageSrc"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Text", path: ["content", "text"] },
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{ type: "text", label: "CTA slug", path: ["content", "ctaHref", "slug"], placeholder: "/path" },
			{ type: "text", label: "CTA external URL", path: ["content", "ctaHref", "external"], placeholder: "https://..." },
			{
				type: "select",
				label: "Callout style",
				path: ["content", "calloutVariant"],
				options: [
					{ label: "Default (blue panel)", value: "default" },
					{ label: "Transparent", value: "transparent" },
					{ label: "Full width bg (#F3F7FC)", value: "fullWidth" }
				]
			},
			{
				type: "select",
				label: "Panel background",
				path: ["content", "panelBackground"],
				options: [
					{ label: "Default blue (#005CA9)", value: "default" },
					{ label: "Light (#EDF7FC)", value: "light" },
					{ label: "Transparent", value: "transparent" }
				]
			},
			{
				type: "select",
				label: "Button color",
				path: ["content", "buttonColor"],
				options: [
					{ label: "Default orange (#F9A21B)", value: "default" },
					{ label: "Blue (#005CA9)", value: "blue" }
				]
			},
			{
				type: "select",
				label: "Image fit text height (desktop/tablet)",
				path: ["content", "imageFitTextHeight"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes — image height matches text panel", value: "true" }
				],
				helper: "When on, both columns share the same height on desktop/tablet; image fills and crops to match. On mobile the image keeps normal visible height (16:9)."
			}
		],
		previewComponents: withPreview(ComponentTypes.SplitImageCallout, {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			title: "Callout title",
			text: "Callout text",
			ctaLabel: "Learn more",
			ctaHref: "#"
		})
	},
	{
		type: ComponentTypes.TextCardGrid,
		title: "Text Card Grid",
		description: "Card grid without icons; each card has title, text, and a down arrow (#FFA401). Content is centered in each card.",
		tags: ["grid", "cards", "card"],
		defaultContent: {
			items: [
				{ title: "Card title 1", text: "Card text 1" },
				{ title: "Card title 2", text: "Card text 2" },
				{ title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "array",
				label: "Cards",
				itemLabel: "Card",
				itemType: "object",
				defaultItem: { title: "", text: "" },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Text", path: ["text"] },
					{ type: "href", label: "Link (optional)", path: ["href"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.TextCardGrid, {
			items: [
				{ title: "Card title 1", text: "Card text 1" },
				{ title: "Card title 2", text: "Card text 2" },
				{ title: "Card title 3", text: "Card text 3" }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3
		})
	},
	{
		type: ComponentTypes.LabeledTextGrid,
		title: "Labeled Text Grid",
		description: "Grid of text blocks: title in #0095DB, body lines in black. 2/3/4 columns for tablet and desktop.",
		tags: ["grid", "text"],
		defaultContent: {
			items: [
				{ title: "Adresse postale", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] },
				{ title: "Livraisons", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2,
			centerLastRow: false
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Center last row",
				path: ["content", "centerLastRow"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "When the last row has fewer items, center them. Default: no."
			},
			{
				type: "array",
				label: "Items",
				itemLabel: "Block",
				itemType: "object",
				defaultItem: { title: "", lines: [] },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"], placeholder: "e.g. Adresse postale" },
					{
						type: "array",
						label: "Lines",
						itemLabel: "Line",
						itemType: "text",
						defaultItem: "",
						path: ["lines"]
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.LabeledTextGrid, {
			items: [
				{ title: "Adresse postale", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] },
				{ title: "Livraisons", lines: ["2697 Kinshasa 1", "République Démocratique du Congo"] }
			],
			cardsPerRowTablet: 2,
			cardsPerRowDesktop: 2
		})
	},
	{
		type: ComponentTypes.CompanyReportGrid,
		title: "Company Report Grid",
		description: "Grid of report cards (bg #0095DB): title, year, published date, download button. Cards per row configurable for tablet/desktop.",
		tags: ["grid", "reports", "documents"],
		defaultContent: {
			items: [
				{ title: "Rapport d'entreprise", year: "2025", publishedDate: "21-11-2025", href: { slug: "/docs/rapport-2025" } },
				{ title: "Rapport d'entreprise", year: "2024", publishedDate: "21-11-2024", href: { slug: "/docs/rapport-2024" } },
				{ title: "Rapport d'entreprise", year: "2023", publishedDate: "21-11-2023", href: { slug: "/docs/rapport-2023" } }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3,
			downloadLabel: "Télécharger",
			centerLastRow: false
		},
		fields: [
			{
				type: "select",
				label: "Cards per row (tablet)",
				path: ["content", "cardsPerRowTablet"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Cards per row (desktop)",
				path: ["content", "cardsPerRowDesktop"],
				options: [
					{ label: "2", value: "2" },
					{ label: "3", value: "3" },
					{ label: "4", value: "4" }
				]
			},
			{
				type: "select",
				label: "Center last row",
				path: ["content", "centerLastRow"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "When the last row has fewer items, center them. Default: no."
			},
			{ type: "text", label: "Download button label", path: ["content", "downloadLabel"], placeholder: "Télécharger" },
			{
				type: "array",
				label: "Report cards",
				itemLabel: "Report",
				itemType: "object",
				defaultItem: { title: "Rapport d'entreprise", year: "", publishedDate: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"], placeholder: "Rapport d'entreprise" },
					{ type: "text", label: "Year", path: ["year"], placeholder: "2025" },
					{ type: "text", label: "Published date", path: ["publishedDate"], placeholder: "21-11-2025" },
					{
						type: "file",
						label: "Pick PDF from storage",
						path: ["href"],
						folder: "documents",
						helper: "Select the report PDF."
					},
					{
						type: "href",
						label: "Or destination (page / PDF URL)",
						path: ["href"]
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.CompanyReportGrid, {
			items: [
				{ title: "Rapport d'entreprise", year: "2025", publishedDate: "21-11-2025", href: { slug: "/docs/rapport-2025" } },
				{ title: "Rapport d'entreprise", year: "2024", publishedDate: "21-11-2024", href: { slug: "/docs/rapport-2024" } },
				{ title: "Rapport d'entreprise", year: "2023", publishedDate: "21-11-2023", href: { slug: "/docs/rapport-2023" } },
				{ title: "Rapport d'entreprise", year: "2022", publishedDate: "21-11-2022", href: { slug: "/docs/rapport-2022" } },
				{ title: "Rapport d'entreprise", year: "2021", publishedDate: "21-11-2021", href: { slug: "/docs/rapport-2021" } },
				{ title: "Rapport d'entreprise", year: "2020", publishedDate: "21-11-2020", href: { slug: "/docs/rapport-2020" } }
			],
			cardsPerRowTablet: 3,
			cardsPerRowDesktop: 3,
			downloadLabel: "Télécharger"
		})
	},
	{
		type: ComponentTypes.TextWithSideImage,
		title: "Text With Side Image",
		description: "Two-column text with portrait image.",
		tags: ["profile", "text"],
		defaultContent: {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Portrait",
			firstParagraph: "Paragraph 1",
			secondParagraph: "Paragraph 2",
			governorName: "Name",
			governorHref: { slug: "/profile" }
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "imageSrc"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "textarea", label: "First paragraph", path: ["content", "firstParagraph"] },
			{ type: "textarea", label: "Second paragraph", path: ["content", "secondParagraph"] },
			{ type: "text", label: "Governor name", path: ["content", "governorName"] },
			{ type: "text", label: "Governor slug", path: ["content", "governorHref", "slug"], placeholder: "/path" },
			{ type: "text", label: "Governor external URL", path: ["content", "governorHref", "external"], placeholder: "https://..." }
		],
		previewComponents: withPreview(ComponentTypes.TextWithSideImage, {
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Portrait",
			firstParagraph: "Paragraph 1",
			secondParagraph: "Paragraph 2",
			governorName: "Name",
			governorHref: "#"
		})
	},
	{
		type: ComponentTypes.TextWithSidePanel,
		title: "Text With Side Panel",
		description: "Two-thirds text with a side panel for media and CTA.",
		tags: ["text", "panel", "image"],
		defaultContent: {
			title: "Section title",
			paragraphs: [
				"Paragraph 1",
				"Paragraph 2",
				"Paragraph 3"
			],
			panelTitle: "Panel title",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			ctaLabel: "Learn more",
			ctaHref: { slug: "/link" }
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{
				type: "array",
				label: "Paragraphs",
				itemLabel: "Paragraph",
				itemType: "text",
				defaultItem: "",
				path: ["content", "paragraphs"],
				itemEditor: "richParagraph"
			},
			{ type: "text", label: "Panel title", path: ["content", "panelTitle"] },
			{
				type: "image",
				label: "Panel image",
				path: ["content", "imageSrc"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "imageAlt"] },
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Select a page or external URL for the button."
			}
		],
		previewComponents: withPreview(ComponentTypes.TextWithSidePanel, {
			title: "Section title",
			paragraphs: [
				"Paragraph 1",
				"Paragraph 2",
				"Paragraph 3"
			],
			panelTitle: "Panel title",
			imageSrc: DEFAULT_PRERENDER_IMAGE,
			imageAlt: "Image",
			ctaLabel: "Learn more",
			ctaHref: "#"
		})
	},
	{
		type: ComponentTypes.BannerImage,
		title: "Banner Image",
		description: "Full-width banner image block.",
		tags: ["image", "banner"],
		defaultContent: {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Banner image",
			showLoadingCircle: false
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "src"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "alt"] },
			{
				type: "select",
				label: "Show loading circle",
				path: ["content", "showLoadingCircle"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "Show a loading circle while the image loads."
			}
		],
		previewComponents: withPreview(ComponentTypes.BannerImage, {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Banner image",
			showLoadingCircle: false
		})
	},
	{
		type: "FullWidthImage" as ComponentType,
		title: "Full Width Image",
		description: "Single image spanning the full width of the content area.",
		tags: ["image", "full-width"],
		defaultContent: {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Full width image",
			showLoadingCircle: false
		},
		fields: [
			{
				type: "image",
				label: "Image src",
				path: ["content", "src"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "alt"] },
			{
				type: "select",
				label: "Show loading circle",
				path: ["content", "showLoadingCircle"],
				options: [
					{ label: "No", value: "false" },
					{ label: "Yes", value: "true" }
				],
				helper: "Show a loading circle while the image loads."
			}
		],
		previewComponents: withPreview("FullWidthImage" as ComponentType, {
			src: DEFAULT_PRERENDER_IMAGE,
			alt: "Full width image",
			showLoadingCircle: false
		})
	},
	{
		type: ComponentTypes.ContactForm,
		title: "Contact Form",
		description: "Contact form with subject options.",
		tags: ["form", "contact"],
		defaultContent: {
			subjectOptions: ["Subject 1", "Subject 2", "Subject 3"],
			contactInfo: {
				address: "Address",
				phone: "Phone",
				email: "Email"
			}
		},
		fields: [
			{
				type: "array",
				label: "Subject options",
				itemLabel: "Option",
				itemType: "text",
				defaultItem: "",
				path: ["content", "subjectOptions"]
			}
		],
		previewComponents: withPreview(ComponentTypes.ContactForm, {
			subjectOptions: ["Subject 1", "Subject 2", "Subject 3"]
		})
	},
	{
		type: ComponentTypes.SeriesExplorer,
		title: "Series Explorer",
		description: "Interactive explorer for series.",
		tags: ["data", "explorer"],
		defaultContent: {
			series: [
				{ id: "series-1", title: "Series 1", category: "category-1", frequency: "mensuel" },
				{ id: "series-2", title: "Series 2", category: "category-2", frequency: "trimestriel" }
			],
			defaultCategory: "category-1",
			defaultFrequency: "mensuel"
		},
		fields: [],
		previewComponents: withPreview(ComponentTypes.SeriesExplorer, {
			series: [
				{ id: "series-1", title: "Series 1", category: "category-1", frequency: "mensuel" },
				{ id: "series-2", title: "Series 2", category: "category-2", frequency: "trimestriel" }
			],
			defaultCategory: "category-1",
			defaultFrequency: "mensuel"
		})
	},
	{
		type: ComponentTypes.KeyMetricsExplorer,
		title: "Key Metrics Explorer",
		description: "Interactive metrics browser.",
		tags: ["data", "explorer"],
		defaultContent: {
			metrics: [
				{ id: "metric-1", category: "category-1", label: "Metric 1", value: "0" },
				{ id: "metric-2", category: "category-2", label: "Metric 2", value: "0" }
			],
			defaultCategory: "category-1"
		},
		fields: [],
		previewComponents: withPreview(ComponentTypes.KeyMetricsExplorer, {
			metrics: [
				{ id: "metric-1", category: "category-1", label: "Metric 1", value: "0" },
				{ id: "metric-2", category: "category-2", label: "Metric 2", value: "0" }
			],
			defaultCategory: "category-1"
		})
	},
	{
		type: ComponentTypes.FxRatesExplorer,
		title: "FX Rates Explorer",
		description: "Currency rates explorer with charts.",
		tags: ["data", "fx", "explorer"],
		defaultContent: {
			rates: [
				{ code: "CUR1", name: "Currency 1", buy: 0, sell: 0, change1d: 0, change7d: 0 },
				{ code: "CUR2", name: "Currency 2", buy: 0, sell: 0, change1d: 0, change7d: 0 }
			],
			defaultFrom: "CUR1",
			defaultTo: "CUR2"
		},
		fields: [],
		previewComponents: withPreview(ComponentTypes.FxRatesExplorer, {
			rates: [
				{ code: "CUR1", name: "Currency 1", buy: 0, sell: 0, change1d: 0, change7d: 0 },
				{ code: "CUR2", name: "Currency 2", buy: 0, sell: 0, change1d: 0, change7d: 0 }
			],
			defaultFrom: "CUR1",
			defaultTo: "CUR2"
		})
	},
	{
		type: ComponentTypes.ArticleHero,
		title: "Article Hero",
		description: "Rich article header with breadcrumb and sharing.",
		tags: ["article", "hero"],
		defaultContent: {
			eyebrow: "Category",
			breadcrumbs: [],
			breadcrumbsSource: "categories",
			breadcrumbsCategoryPathIds: [],
			breadcrumbsCategoryPath: "",
			breadcrumbsLink: { slug: "" },
			title: "Article title",
			summary: "Article summary",
			author: "Author",
			authorRole: "Role",
			publishDate: "01/2025",
			featuredImage: {
				src: DEFAULT_PRERENDER_IMAGE,
				alt: "Image"
			}
		},
		fields: [
			{ type: "text", label: "Eyebrow", path: ["content", "eyebrow"], helper: "Optional label above the title." },
			{
				type: "select",
				label: "Breadcrumbs source",
				path: ["content", "breadcrumbsSource"],
				options: [
					{ label: "Category path", value: "categories" },
					{ label: "Dynamic categories", value: "dynamic" }
				],
				helper: "Category path = select a category path. Dynamic = use dynamic content categories."
			},
			{
				type: "categoryPath",
				label: "Category path",
				path: ["content", "breadcrumbsCategoryPathIds"],
				helper: "Select any category node to auto-build breadcrumbs from its path."
			},
			{
				type: "text",
				label: "Category path (variable)",
				path: ["content", "breadcrumbsCategoryPath"],
				helper: "Optional key path or variable (ex: key1/key2 or {categories}). If set, it overrides the selected category path."
			},
			{
				type: "href",
				label: "Breadcrumbs link",
				path: ["content", "breadcrumbsLink"],
				helper: "Base link for all breadcrumb items. Category path is appended as ?category=key1/key2."
			},
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Summary", path: ["content", "summary"] },
			{ type: "text", label: "Author", path: ["content", "author"] },
			{ type: "text", label: "Role", path: ["content", "authorRole"] },
			{ type: "text", label: "Publish date", path: ["content", "publishDate"] },
			{
				type: "image",
				label: "Image src",
				path: ["content", "featuredImage", "src"],
				helper: "Select an image from storage (CDN key only)."
			},
			{ type: "text", label: "Image alt", path: ["content", "featuredImage", "alt"] }
		],
		previewComponents: withPreview(ComponentTypes.ArticleHero, {
			eyebrow: "Category",
			title: "Article title",
			summary: "Article summary",
			author: "Author",
			authorRole: "Role",
			publishDate: "01/2025",
			featuredImage: {
				src: DEFAULT_PRERENDER_IMAGE,
				alt: "Image"
			}
		})
	},
	{
		type: ComponentTypes.BlogArticleGrid,
		title: "Blog Article Grid",
		description: "Grid of blog articles with CTA.",
		tags: ["blog", "grid"],
		defaultContent: {
			items: [
				{
					id: "blog-1",
					title: "Blog title 1",
					excerpt: "Blog excerpt 1",
					href: { slug: "/blog/post-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					id: "blog-2",
					title: "Blog title 2",
					excerpt: "Blog excerpt 2",
					href: { slug: "/blog/post-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View blog",
			ctaHref: { slug: "/blog" }
		},
		fields: [
			{
				type: "array",
				label: "Articles",
				itemLabel: "Article",
				itemType: "object",
				defaultItem: { title: "", excerpt: "", href: { slug: "" }, imageSrc: DEFAULT_PRERENDER_IMAGE },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "textarea", label: "Excerpt", path: ["excerpt"] },
					{
						type: "href",
						label: "Destination",
						path: ["href"],
						helper: "Select a blog page or external URL."
					},
					{
						type: "image",
						label: "Image src",
						path: ["imageSrc"],
						helper: "Select an image from storage (CDN key only)."
					}
				]
			},
			{ type: "text", label: "CTA label", path: ["content", "ctaLabel"] },
			{
				type: "href",
				label: "CTA destination",
				path: ["content", "ctaHref"],
				helper: "Choose the blog index or another target."
			}
		],
		previewComponents: withPreview(ComponentTypes.BlogArticleGrid, {
			items: [
				{
					id: "blog-1",
					title: "Blog title 1",
					excerpt: "Blog excerpt 1",
					href: { slug: "/blog/post-1" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				},
				{
					id: "blog-2",
					title: "Blog title 2",
					excerpt: "Blog excerpt 2",
					href: { slug: "/blog/post-2" },
					imageSrc: DEFAULT_PRERENDER_IMAGE
				}
			],
			ctaLabel: "View blog",
			ctaHref: { slug: "/blog" }
		})
	},
	{
		type: ComponentTypes.NewsletterSignup,
		title: "Newsletter Signup",
		description: "Email capture with consent text.",
		tags: ["form", "newsletter"],
		defaultContent: {
			title: "Newsletter title",
			description: "Newsletter description",
			consentLabel: "Consent label",
			emailPlaceholder: "Email",
			requiredNote: "Required",
			submitLabel: "Submit"
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Description", path: ["content", "description"] },
			{ type: "text", label: "Consent label", path: ["content", "consentLabel"] },
			{ type: "text", label: "Email placeholder", path: ["content", "emailPlaceholder"] },
			{ type: "text", label: "Required note", path: ["content", "requiredNote"] },
			{ type: "text", label: "Submit label", path: ["content", "submitLabel"] }
		],
		previewComponents: withPreview(ComponentTypes.NewsletterSignup, {
			title: "Newsletter title",
			description: "Newsletter description",
			consentLabel: "Consent label",
			emailPlaceholder: "Email",
			requiredNote: "Required",
			submitLabel: "Submit"
		})
	},
	{
		type: ComponentTypes.SingleLink,
		title: "Single Link",
		description: "Simple text link block.",
		tags: ["link"],
		defaultContent: {
			href: { slug: "/link" },
			label: "Link label"
		},
		fields: [
			{ type: "text", label: "Label", path: ["content", "label"] },
			{
				type: "href",
				label: "Destination",
				path: ["content", "href"],
				helper: "Select a page, type a path, or paste an external URL."
			}
		],
		previewComponents: withPreview(ComponentTypes.SingleLink, { href: { slug: "/link" }, label: "Link label" })
	},
	{
		type: ComponentTypes.LegislationList,
		title: "Legislation / Document List",
		description: "List of underlined links with download icon (#FFA401).",
		tags: ["list", "links", "legislation", "documents"],
		defaultContent: {
			items: [
				{ label: "Loi organique n° 18-027 du 13 décembre 2018", href: { slug: "/docs/loi-18-027" } },
				{ label: "Statuts de la Banque Centrale du Congo", href: { slug: "/docs/statuts" } }
			]
		},
		fields: [
			{
				type: "array",
				label: "Document links",
				itemLabel: "Link",
				itemType: "object",
				defaultItem: { label: "", href: { slug: "" } },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{
						type: "file",
						label: "Pick PDF from storage",
						path: ["href"],
						folder: "documents",
						helper: "Select a PDF from the documents folder. Clicking the label or download icon will open this file."
					},
					{
						type: "href",
						label: "Or destination (page / PDF URL)",
						path: ["href"],
						helper: "Alternatively choose a page or paste a PDF URL. Overwrites the PDF picker if set."
					}
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.LegislationList, {
			items: [
				{ label: "Loi organique n° 18-027 du 13 décembre 2018", href: { slug: "/docs/loi-18-027" } },
				{ label: "Statuts de la Banque Centrale du Congo", href: { slug: "/docs/statuts" } },
				{ label: "Convention entre l'État congolais et la Banque centrale", href: { slug: "/docs/convention" } }
			]
		})
	},
	{
		type: ComponentTypes.NumberedList,
		title: "Numbered List",
		description: "Ordered list (1. 2. 3. …). Each item is a string; long items wrap to multiple lines.",
		tags: ["list", "text"],
		defaultContent: {
			items: [
				"First item.",
				"Second item.",
				"Third item."
			]
		},
		fields: [
			{
				type: "array",
				label: "List items",
				itemLabel: "Item",
				itemType: "text",
				defaultItem: "",
				path: ["content", "items"]
			}
		],
		previewComponents: withPreview(ComponentTypes.NumberedList, {
			items: [
				"la mise en oeuvre de la politique monétaire de la Banque;",
				"l'octroi des crédits spéciaux visés à l'article 20 de loi organique n°18-027;",
				"les décisions en matière d'agrément des établissements de crédit et autres établissements financiers assujettis au contrôle de la Banque et la prise de toute mesure, sanction, ordre ou décision en vertu des législations de contrôle concernées;",
				"l'édiction des instructions;",
				"l'adoption du budget et les comptes annuels;",
				"les nominations et les promotions du personnel;",
				"l'organisation du régime complémentaire de retraite du personnel."
			]
		})
	},
	{
		type: ComponentTypes.TwoColumnList,
		title: "Two-Column Text List",
		description: "List of rows: left text (bg #0095DB, white) and right text (bg #EDF7FC, #005CA9).",
		tags: ["list", "text"],
		defaultContent: {
			items: [
				{ left: "Left 1", right: "Right 1" },
				{ left: "Left 2", right: "Right 2" }
			]
		},
		fields: [
			{
				type: "array",
				label: "Rows",
				itemLabel: "Row",
				itemType: "object",
				defaultItem: { left: "", right: "" },
				path: ["content", "items"],
				fields: [
					{ type: "text", label: "Left text", path: ["left"] },
					{ type: "text", label: "Right text", path: ["right"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.TwoColumnList, {
			items: [
				{ left: "Jeudi 1er janvier", right: "Nouvel an" },
				{ left: "Dimanche 4 janvier", right: "Journée des Martyrs" },
				{ left: "Vendredi 16 janvier", right: "Assassinat de Laurent-Désiré Kabila" },
				{ left: "Samedi 17 janvier", right: "Assassinat de Patrice Lumumba" },
				{ left: "Lundi 6 avril", right: "Kimbangu Day" },
				{ left: "Vendredi 1er mai", right: "Fête du Travail" },
				{ left: "Dimanche 17 mai", right: "Journée de la Libération / Forces armées" },
				{ left: "Mardi 30 juin", right: "Jour de l'Indépendance" },
				{ left: "Samedi 1er août", right: "Journée des Parents" },
				{ left: "Vendredi 25 décembre", right: "Noël" }
			]
		})
	},
	// =====================================================================
	// MarketsBoard — three sub-cards: Exchange / Inflation / Interest.
	// All three are manually authored today; only `inflation` exposes a
	// manual ↔ dynamic mode toggle that mirrors the NewsSection pattern.
	// `exchange` and `interest` keep the same nested shape so adding their
	// own dynamic modes later is purely additive (no schema migration).
	// Live preview for inflation dynamic mode is wired in
	// `useDynamicContentResolvers.resolveMarketsBoardDynamicContent`.
	// =====================================================================
	{
		type: ComponentTypes.MarketsBoard,
		title: "Markets Board",
		description: "Three-up panel: Cours de change, Taux d'inflation, Taux d'intérêt.",
		tags: ["home", "markets", "indicators", "dynamic"],
		defaultContent: {
			title: "Marchés et indicateurs",
			subtitle: "Données clés mises à jour quotidiennement par la Banque Centrale du Congo.",
			actionLabel: "Convertisseur de taux",
			actionHref: { slug: "/chiffres-cles" },
			exchange: {
				title: "Cours de change",
				asOf: "",
				featured: { label: "1 USD = 0,0000 CDF", delta1d: "", delta2d: "", caption: "cours indicatif", trend: "flat" },
				rows: [],
				sparkline: [],
				moreLabel: "Plus de détails",
				moreHref: { slug: "/cours-de-change" }
			},
			inflation: {
				title: "Taux d'inflation",
				unitCaption: "(valeurs en %)",
				mode: "manual",
				// Default mapping mirrors the canonical key names so the
				// dropdowns show real values right away when an editor
				// switches to dynamic mode (rather than a sea of "None"s).
				// The projector treats empty entries as "use the default
				// key" anyway, so persisted documents that never had
				// mapping keep working.
				dynamicComponent: {
					contentTypeKey: "",
					sortField: "date",
					fieldMapping: {
						date: "date",
						source: "source",
						hebdomadaire: "hebdomadaire",
						"cumule-annee": "cumule-annee",
						"glissement-annuel": "glissement-annuel",
						annualise: "annualise"
					},
					sparklineField: "annualise",
					sparklineWindow: 8
				},
				asOf: "",
				source: "INS",
				rows: [],
				sparkline: [],
				moreLabel: "Tous les indicateurs",
				moreHref: { slug: "/statistiques" }
			},
			interest: {
				title: "Taux d'intérêt",
				directeur: { title: "Taux Directeur", asOfLabel: "Depuis le", asOf: "", rows: [] },
				bons: { title: "Bons APA", asOfLabel: "Du", asOf: "", columns: { jours: "JOURS", tmp: "TMP", tm: "TM" }, rows: [] },
				moreLabel: "Tous les taux",
				moreHref: { slug: "/taux-interet" }
			}
		},
		fields: [
			{ type: "text", label: "Title", path: ["content", "title"] },
			{ type: "textarea", label: "Subtitle", path: ["content", "subtitle"] },
			{ type: "text", label: "Action label", path: ["content", "actionLabel"] },
			{ type: "href", label: "Action link", path: ["content", "actionHref"] },

			// ---- Cours de change (manual only for now; structure kept identical to inflation
			//      so adding mode toggle later is a one-line change) ----
			{
				type: "object",
				label: "Cours de change",
				path: ["content", "exchange"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "As-of", path: ["asOf"], helper: "Free text date/time stamp shown in the card header." },
					{
						type: "object",
						label: "Featured",
						path: ["featured"],
						fields: [
							{ type: "text", label: "Label", path: ["label"] },
							{ type: "text", label: "Delta 1 day", path: ["delta1d"] },
							{ type: "text", label: "Delta 2 days", path: ["delta2d"] },
							{ type: "text", label: "Caption", path: ["caption"] },
							{
								type: "select",
								label: "Trend",
								path: ["trend"],
								options: [
									{ label: "Up", value: "up" },
									{ label: "Down", value: "down" },
									{ label: "Flat", value: "flat" }
								]
							}
						]
					},
					{
						type: "array",
						label: "Currency rows",
						itemLabel: "Row",
						itemType: "object",
						defaultItem: { code: "", value: "", flag: "", trend: "flat" },
						path: ["rows"],
						fields: [
							{ type: "text", label: "Code", path: ["code"], helper: "ISO 4217 (USD, EUR, …)" },
							{ type: "text", label: "Value", path: ["value"] },
							{ type: "text", label: "Flag (ISO-2)", path: ["flag"], helper: "Lowercase 2-letter (us, eu, gb, cn)" },
							{
								type: "select",
								label: "Trend",
								path: ["trend"],
								options: [
									{ label: "Up", value: "up" },
									{ label: "Down", value: "down" },
									{ label: "Flat", value: "flat" }
								]
							}
						]
					},
					{ type: "text", label: "More label", path: ["moreLabel"] },
					{ type: "href", label: "More link", path: ["moreHref"] }
				]
			},

			// ---- Taux d'inflation (manual ↔ dynamic) ----
			{
				type: "object",
				label: "Taux d'inflation",
				path: ["content", "inflation"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{ type: "text", label: "Unit caption", path: ["unitCaption"] },
					{
						type: "select",
						label: "Mode",
						path: ["mode"],
						options: [
							{ label: "Manual", value: "manual" },
							{ label: "Dynamic", value: "dynamic" }
						],
						helper: "Manual uses the rows below. Dynamic pulls the latest record of a content type at deploy time."
					},
					{
						type: "object",
						label: "Dynamic settings",
						path: ["dynamicComponent"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "dynamic" },
						fields: [
							{
								type: "contentTypeSelect",
								label: "Content type",
								path: ["contentTypeKey"],
								helper: "Select the content type whose latest record drives the card."
							},
							{
								type: "contentFieldSelect",
								label: "Sort field (date)",
								path: ["sortField"],
								contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"],
								fieldTypes: ["date"],
								includeBaseFields: false,
								allowEmpty: true,
								valueFormat: "raw",
								helper: "Newest record by this field becomes the displayed snapshot."
							},
							{
								type: "object",
								label: "Field mapping",
								path: ["fieldMapping"],
								helper: "Override which fields supply each row. Empty = use the default key.",
								fields: [
									{ type: "contentFieldSelect", label: "Date field",        path: ["date"],              contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Source field",      path: ["source"],            contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Hebdomadaire",      path: ["hebdomadaire"],      contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Cumulé année",      path: ["cumule-annee"],      contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Glissement annuel", path: ["glissement-annuel"], contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" },
									{ type: "contentFieldSelect", label: "Annualisé",         path: ["annualise"],         contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"], includeBaseFields: false, allowEmpty: true, valueFormat: "raw" }
								]
							},
							// Sparkline projection — author picks one numeric
							// field; the deploy projector plots its last N
							// values (oldest → newest). Replaces the
							// previously-hardcoded fake series.
							{
								type: "contentFieldSelect",
								label: "Sparkline field",
								path: ["sparklineField"],
								contentTypePath: ["content", "inflation", "dynamicComponent", "contentTypeKey"],
								includeBaseFields: false,
								allowEmpty: true,
								valueFormat: "raw",
								helper: "Numeric field to chart (last N records). Empty = no sparkline."
							},
							{
								type: "number",
								label: "Sparkline window",
								path: ["sparklineWindow"],
								helper: "How many recent records the sparkline plots (default 8)."
							}
						]
					},
					{
						type: "text",
						label: "As-of",
						path: ["asOf"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "manual" }
					},
					{
						type: "text",
						label: "Source",
						path: ["source"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "manual" }
					},
					{
						type: "array",
						label: "Rows",
						itemLabel: "Row",
						itemType: "object",
						defaultItem: { label: "", value: "" },
						path: ["rows"],
						showWhen: { path: ["content", "inflation", "mode"], equals: "manual" },
						fields: [
							{ type: "text", label: "Label", path: ["label"] },
							{ type: "text", label: "Value", path: ["value"] }
						]
					},
					{ type: "text", label: "More label", path: ["moreLabel"] },
					{ type: "href", label: "More link", path: ["moreHref"] }
				]
			},

			// ---- Taux d'intérêt (manual only for now; same pattern as inflation
			//      to make adding dynamic mode later a one-line addition) ----
			{
				type: "object",
				label: "Taux d'intérêt",
				path: ["content", "interest"],
				fields: [
					{ type: "text", label: "Title", path: ["title"] },
					{
						type: "object",
						label: "Taux Directeur",
						path: ["directeur"],
						fields: [
							{ type: "text", label: "Title", path: ["title"] },
							{ type: "text", label: "As-of label", path: ["asOfLabel"], helper: "Prefix shown next to the date (e.g. \"Depuis le\")." },
							{ type: "text", label: "As-of", path: ["asOf"] },
							{
								type: "array",
								label: "Rows",
								itemLabel: "Row",
								itemType: "object",
								defaultItem: { label: "", value: "", trend: "flat" },
								path: ["rows"],
								fields: [
									{ type: "text", label: "Label", path: ["label"] },
									{ type: "text", label: "Value", path: ["value"] },
									{
										type: "select",
										label: "Trend",
										path: ["trend"],
										options: [
											{ label: "Up", value: "up" },
											{ label: "Down", value: "down" },
											{ label: "Flat", value: "flat" }
										]
									}
								]
							}
						]
					},
					{
						type: "object",
						label: "Bons APA",
						path: ["bons"],
						fields: [
							{ type: "text", label: "Title", path: ["title"] },
							{ type: "text", label: "As-of label", path: ["asOfLabel"] },
							{ type: "text", label: "As-of", path: ["asOf"] },
							{
								type: "object",
								label: "Column headers",
								path: ["columns"],
								fields: [
									{ type: "text", label: "Jours", path: ["jours"] },
									{ type: "text", label: "TMP",   path: ["tmp"] },
									{ type: "text", label: "TM",    path: ["tm"] }
								]
							},
							{
								type: "array",
								label: "Rows",
								itemLabel: "Row",
								itemType: "object",
								defaultItem: { jours: "", tmp: "", tm: "" },
								path: ["rows"],
								fields: [
									{ type: "text", label: "Jours", path: ["jours"] },
									{ type: "text", label: "TMP",   path: ["tmp"] },
									{ type: "text", label: "TM",    path: ["tm"] }
								]
							}
						]
					},
					{ type: "text", label: "More label", path: ["moreLabel"] },
					{ type: "href", label: "More link", path: ["moreHref"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.MarketsBoard, {
			title: "Marchés et indicateurs",
			subtitle: "Données clés mises à jour quotidiennement par la Banque Centrale du Congo.",
			actionLabel: "Convertisseur de taux",
			actionHref: { slug: "/chiffres-cles" },
			exchange: {
				title: "Cours de change",
				asOf: "—",
				featured: { label: "1 USD = 0,0000 CDF", delta1d: "0,00 % · 1jr", delta2d: "0,00 % · 2jrs", caption: "cours indicatif", trend: "flat" },
				rows: [
					{ code: "USD", value: "0,0000", flag: "us", trend: "flat" },
					{ code: "EUR", value: "0,0000", flag: "eu", trend: "flat" }
				],
				sparkline: [],
				moreLabel: "Plus de détails",
				moreHref: { slug: "/cours-de-change" }
			},
			inflation: {
				title: "Taux d'inflation",
				unitCaption: "(valeurs en %)",
				mode: "manual",
				asOf: "—",
				source: "INS",
				rows: [
					{ label: "Hebdomadaire", value: "0,000" },
					{ label: "Cumulé de l'année", value: "0,000" },
					{ label: "Glissement annuel", value: "0,000" },
					{ label: "Annualisé", value: "0,000" }
				],
				moreLabel: "Tous les indicateurs",
				moreHref: { slug: "/statistiques" }
			},
			interest: {
				title: "Taux d'intérêt",
				directeur: { title: "Taux Directeur", asOfLabel: "Depuis le", asOf: "—", rows: [] },
				bons: { title: "Bons APA", asOfLabel: "Du", asOf: "—", columns: { jours: "JOURS", tmp: "TMP", tm: "TM" }, rows: [] },
				moreLabel: "Tous les taux",
				moreHref: { slug: "/taux-interet" }
			}
		})
	},
	// =====================================================================
	// DataRecordDetail — generic detail block for one dated data record.
	// Authored once as a dynamic-path template page: typeLabel is static,
	// recordDate / each field value / pdfKey / body carry `{token}`
	// placeholders the deploy engine substitutes per record. Unsubstituted
	// `{token}` values are skipped by the renderer at display time.
	// =====================================================================
	{
		type: ComponentTypes.DataRecordDetail,
		title: "Data Record Detail",
		description: "Detail block for one dated data record (header, field table, PDF, body). Template-only.",
		tags: ["data", "record", "dynamic", "template"],
		defaultContent: {
			typeLabel: "Cours de change",
			recordDate: "{date}",
			fields: [
				{ label: "Libellé", value: "{valeur}" }
			],
			pdfKey: "",
			body: "",
			sourceLabel: ""
		},
		fields: [
			{
				type: "text",
				label: "Type label",
				path: ["content", "typeLabel"],
				helper: "Static content-type label shown in the header band (e.g. \"Cours de change\")."
			},
			{
				type: "text",
				label: "Record date",
				path: ["content", "recordDate"],
				helper: "Display date. In a template use a token, e.g. {date}."
			},
			{
				type: "array",
				label: "Fields",
				itemLabel: "Field",
				itemType: "object",
				defaultItem: { label: "", value: "" },
				path: ["content", "fields"],
				fields: [
					{ type: "text", label: "Label", path: ["label"] },
					{ type: "text", label: "Value", path: ["value"], helper: "In a template use a token, e.g. {valeur}." }
				]
			},
			{
				type: "text",
				label: "PDF key",
				path: ["content", "pdfKey"],
				helper: "Storage key of an attached PDF. In a template use a token, e.g. {fichier}. Leave empty for none."
			},
			{
				type: "textarea",
				label: "Body",
				path: ["content", "body"],
				helper: "Optional rich-text body. In a template use a token, e.g. {body}."
			},
			{
				type: "text",
				label: "Source label",
				path: ["content", "sourceLabel"],
				helper: "Optional footer note (e.g. \"Source : INS\")."
			}
		],
		previewComponents: withPreview(ComponentTypes.DataRecordDetail, {
			typeLabel: "Cours de change",
			recordDate: "2 janvier 2024",
			fields: [
				{ label: "USD", value: "2 500,00" },
				{ label: "EUR", value: "2 750,50" }
			],
			pdfKey: "",
			body: "",
			sourceLabel: "Source : Banque Centrale du Congo"
		})
	},
	// =====================================================================
	// RichText — renders editorial body HTML faithfully (headings, lists,
	// links, images, quotes, tables). Used for migrated legacy editorial
	// pages where the Paragraph block's sanitiser would strip the markup.
	// =====================================================================
	{
		type: ComponentTypes.RichText,
		title: "Rich Text",
		description: "Renders body HTML faithfully — headings, lists, links, images, quotes, tables.",
		tags: ["text", "content", "html", "editorial"],
		defaultContent: {
			html: "<h2>Titre</h2><p>Contenu de la page.</p>"
		},
		fields: [
			{
				type: "textarea",
				label: "Body HTML",
				path: ["content", "html"],
				helper: "Editorial body HTML. Sanitised on render (scripts, styles and unsafe attributes are removed)."
			}
		],
		previewComponents: withPreview(ComponentTypes.RichText, {
			html: "<h2>Titre</h2><p>Contenu de la page.</p><ul><li>Premier élément</li><li>Deuxième élément</li></ul>"
		})
	},
	// =====================================================================
	// Gallery — a responsive captioned image grid. Used by the migrated
	// legacy "Gallérie des photos" pages and banknote galleries.
	// =====================================================================
	{
		type: ComponentTypes.Gallery,
		title: "Gallery",
		description: "A responsive captioned image grid for photo and banknote galleries.",
		tags: ["image", "gallery", "media", "editorial"],
		defaultContent: {
			images: [{ src: "", caption: "" }]
		},
		fields: [
			{
				type: "number",
				label: "Columns",
				path: ["content", "columns"],
				helper: "Optional column count (1–4). Leave empty for a responsive layout."
			},
			{
				type: "array",
				label: "Images",
				itemLabel: "Image",
				itemType: "object",
				defaultItem: { src: "", caption: "" },
				path: ["content", "images"],
				fields: [
					{
						type: "image",
						label: "Image",
						path: ["src"],
						helper: "Pick an image from storage (CDN key only)."
					},
					{ type: "text", label: "Caption", path: ["caption"] }
				]
			}
		],
		previewComponents: withPreview(ComponentTypes.Gallery, {
			images: [
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Première photo" },
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Deuxième photo" },
				{ src: DEFAULT_PRERENDER_IMAGE, caption: "Troisième photo" }
			]
		})
	}
];

export const componentDefinitionMap = new Map(componentLibrary.map((definition) => [definition.type, definition]));
